import { apiRequest, apiRequestWithEtag, type IEtagged } from '@/domains/api';
import type {
  DdMeetingKind,
  IDdCompletionBody,
  IDdInterview,
  IDdInterviewBody,
  IDdNotesPreview,
  IDdCallScheduleBody,
  IDdOpenBody,
  IDdTemplate,
  IDdTemplateBody,
  IDdTemplatePreviewBody,
  IOutreachAttempt,
  IOutreachBody
} from './dd-contracts';

/**
 * survey-api's due diligence — `/api/v1/admin/…/dd-interviews`, `dd-templates` and `outreach` —
 * and the sole reader of it. An interview's revision travels as an opaque `ETag` quoted back in
 * `If-Match`, as a team member's does (`content/api/team-api.ts`); a stale one is 409
 * `revision-conflict`. Nothing here decides what a completion may carry: survey-api names every
 * problem by its member.
 */

const ADMIN = '/api/v1/admin';

function interviewPath(interviewId: string): string {
  return `${ADMIN}/dd-interviews/${encodeURIComponent(interviewId)}`;
}

function participantPath(participantId: string, part: string): string {
  return `${ADMIN}/participants/${encodeURIComponent(participantId)}/${part}`;
}

function survey(code: string): string {
  return `?survey=${encodeURIComponent(code)}`;
}

function precondition(etag: string): { 'If-Match': string } {
  return { 'If-Match': etag };
}

export const ddApi = {
  /** Every cycle's interview of the pair, newest cycle first. */
  interviews(participantId: string, surveyCode: string, signal?: AbortSignal): Promise<IDdInterview[]> {
    return apiRequest<IDdInterview[]>(`${participantPath(participantId, 'dd-interviews')}${survey(surveyCode)}`, {
      signal
    });
  },

  /** Opens this cycle's — the cycle of the pair's newest live response — 201. 409
   *  `dd-interview-exists` names the one there is, `no-live-response` a pair with no response. */
  open(participantId: string, surveyCode: string): Promise<IEtagged<IDdInterview>> {
    return ddApi.openMeeting(participantId, { survey: surveyCode });
  },

  /** SR-501: a new meeting of a non-`dd_call` kind in the cycle of the newest live response — booked
   *  and assigned when the body says so ("Schedule in-person interview"), bare for "Log a meeting".
   *  400 `interviewerAccountId` names an account that is no active staff. */
  openMeeting(participantId: string, body: IDdOpenBody): Promise<IEtagged<IDdInterview>> {
    return apiRequestWithEtag<IDdInterview>(participantPath(participantId, 'dd-interviews'), {
      method: 'POST',
      body
    });
  },

  /** SR-501: a meeting that is not completed called off, read-only after; 409 `invalid-transition`
   *  on a `dd_call` or a completed one. */
  cancel(interviewId: string, etag: string, reason: Nullable<string>): Promise<IEtagged<IDdInterview>> {
    return apiRequestWithEtag<IDdInterview>(`${interviewPath(interviewId)}/cancellation`, {
      method: 'POST',
      body: { reason },
      headers: precondition(etag)
    });
  },

  /** SR-424's "DD call scheduled": the date set (or cleared, `null`) on this cycle's interview,
   *  which survey-api opens where there is none. No `If-Match` — it may have no revision to quote.
   *  A clear with no interview answers 204, read here as `null`. `interviewerAccountId` (SR-525)
   *  sets who will hold the call — 400 naming it where the account is no active staff. */
  scheduleCall(
    participantId: string,
    surveyCode: string,
    scheduledAt: Nullable<string>,
    note: Nullable<string> = null,
    interviewerAccountId: Nullable<string> = null
  ): Promise<Nullable<IDdInterview>> {
    const body: IDdCallScheduleBody = { survey: surveyCode, scheduledAt, note };
    if (interviewerAccountId) body.interviewerAccountId = interviewerAccountId;
    return apiRequest<Nullable<IDdInterview>>(participantPath(participantId, 'dd-call-schedule'), {
      method: 'PUT',
      body
    });
  },

  /** SR-460's *Confirm clear for DD*: the disclosures recorded as checked and clear on this
   *  cycle's interview, which survey-api opens where there is none — 200 with it. No `If-Match`,
   *  as the scheduling. 409 `open-compliance-items` (`count`) while the compliance queue holds an
   *  unclosed item, `invalid-transition` on a completed call, `no-live-response` with no cycle. */
  clearDisclosures(participantId: string, surveyCode: string, note: Nullable<string>): Promise<IDdInterview> {
    return apiRequest<IDdInterview>(participantPath(participantId, 'dd-disclosure-clearances'), {
      method: 'POST',
      body: { survey: surveyCode, note }
    });
  },

  read(interviewId: string, signal?: AbortSignal): Promise<IEtagged<IDdInterview>> {
    return apiRequestWithEtag<IDdInterview>(interviewPath(interviewId), { signal });
  },

  save(interviewId: string, etag: string, body: IDdInterviewBody): Promise<IEtagged<IDdInterview>> {
    return apiRequestWithEtag<IDdInterview>(interviewPath(interviewId), {
      method: 'PUT',
      body,
      headers: precondition(etag)
    });
  },

  /** RNK-16 AC1/AC3: the answers, the score into Quality rating and the DD call flag, at once. */
  complete(interviewId: string, etag: string, body: IDdCompletionBody): Promise<IEtagged<IDdInterview>> {
    return apiRequestWithEtag<IDdInterview>(`${interviewPath(interviewId)}/completion`, {
      method: 'POST',
      body,
      headers: precondition(etag)
    });
  },

  reopen(interviewId: string, etag: string, reason: string): Promise<IEtagged<IDdInterview>> {
    return apiRequestWithEtag<IDdInterview>(`${interviewPath(interviewId)}/reopening`, {
      method: 'POST',
      body: { reason },
      headers: precondition(etag)
    });
  },

  /** What pasted notes would fill, nothing written. */
  previewNotes(interviewId: string, text: string): Promise<IDdNotesPreview> {
    return apiRequest<IDdNotesPreview>(`${interviewPath(interviewId)}/note-previews`, {
      method: 'POST',
      body: { text }
    });
  },

  importNotes(interviewId: string, etag: string, text: string): Promise<IEtagged<IDdInterview>> {
    return apiRequestWithEtag<IDdInterview>(`${interviewPath(interviewId)}/note-imports`, {
      method: 'POST',
      body: { text },
      headers: precondition(etag)
    });
  },


  /** The survey's versions of one meeting kind's template (SR-501): its draft first where there is
   *  one (SR-525), then the versions newest first; none stored answers the kind's default as
   *  version 0. */
  templates(surveyCode: string, kind: DdMeetingKind, signal?: AbortSignal): Promise<IDdTemplate[]> {
    return apiRequest<IDdTemplate[]>(
      `${ADMIN}/dd-templates${survey(surveyCode)}&kind=${encodeURIComponent(kind)}`,
      { signal }
    );
  },

  /** `internal_fields.author`: the body's kind's next version, its last retired. */
  publishTemplate(body: IDdTemplateBody): Promise<IDdTemplate> {
    return apiRequest<IDdTemplate>(`${ADMIN}/dd-templates`, { method: 'POST', body });
  },

  /** SR-525, `internal_fields.author`: the kind's draft created or replaced, validated as a
   *  publish is — 400 naming each member. Publishing deletes it. */
  saveTemplateDraft(body: IDdTemplateBody): Promise<IDdTemplate> {
    return apiRequest<IDdTemplate>(`${ADMIN}/dd-templates/draft`, { method: 'PUT', body });
  },

  /** SR-525: the kind's draft discarded — 404 where there is none. */
  async discardTemplateDraft(surveyCode: string, kind: DdMeetingKind): Promise<void> {
    await apiRequest<null>(`${ADMIN}/dd-templates/draft${survey(surveyCode)}&kind=${encodeURIComponent(kind)}`, {
      method: 'DELETE'
    });
  },

  /** SR-525: what a Granola sample would fill against an unsaved template, nothing written —
   *  `previewNotes`' answer. */
  previewTemplate(body: IDdTemplatePreviewBody): Promise<IDdNotesPreview> {
    return apiRequest<IDdNotesPreview>(`${ADMIN}/dd-templates/previews`, { method: 'POST', body });
  },

  outreach(participantId: string, surveyCode: string, signal?: AbortSignal): Promise<IOutreachAttempt[]> {
    return apiRequest<IOutreachAttempt[]>(`${participantPath(participantId, 'outreach')}${survey(surveyCode)}`, {
      signal
    });
  },

  recordOutreach(participantId: string, body: IOutreachBody): Promise<IOutreachAttempt> {
    return apiRequest<IOutreachAttempt>(participantPath(participantId, 'outreach'), { method: 'POST', body });
  },

  /** Its recorder's alone: 403 `not-outreach-author` for anyone else. */
  async withdrawOutreach(outreachId: string): Promise<void> {
    await apiRequest<null>(`${ADMIN}/outreach/${encodeURIComponent(outreachId)}`, { method: 'DELETE' });
  }
};
